import dayjs from 'dayjs';
import { listUserRecurringDates } from '../utils/recurringSchedule';

/** Season length shown on the starter card. Week 15's first day is included. */
export const STARTER_PLAN_WEEKS = 15;

/** Offer the plan only on accounts created inside this window. */
export const STARTER_PLAN_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

const CHOICE_PREFIX = 'happy-tomato-starter-plan:';

/**
 * Tomato-from-seed outline. `week` is 1-based from the start day.
 * Repeating tasks run from that week through the end of week 15.
 * Cadence is chosen so the season is 50 tasks: 4 one-offs, 33 waterings,
 * 9 pinches, and 4 feeds.
 */
export const TOMATO_STARTER_TASKS = [
  { id: 'sow', nameKey: 'starter.sowIndoors', icon: 'grass', week: 1 },
  { id: 'pot', nameKey: 'starter.potOn', icon: 'yard', week: 3 },
  { id: 'harden', nameKey: 'starter.hardenOff', icon: 'wb_sunny', week: 5 },
  { id: 'transplant', nameKey: 'starter.transplant', icon: 'park', week: 6 },
  {
    id: 'pinch',
    nameKey: 'starter.pinch',
    icon: 'content_cut',
    week: 7,
    everyDays: 7,
    whenKey: 'starter.weekly',
  },
  {
    id: 'feed',
    nameKey: 'starter.feed',
    icon: 'science',
    week: 8,
    everyDays: 14,
    whenKey: 'starter.every2Weeks',
  },
  {
    id: 'water',
    nameKey: 'starter.water',
    icon: 'water_drop',
    week: 1,
    everyDays: 3,
    whenKey: 'starter.every3Days',
  },
];

export function starterPlanBounds(startDate) {
  const startDay = dayjs(startDate).startOf('day');
  const endDay = startDay.add(STARTER_PLAN_WEEKS - 1, 'week');
  return { startDay, endDay };
}

export function expandStarterPlan(startDate, tasks = TOMATO_STARTER_TASKS) {
  const { startDay, endDay } = starterPlanBounds(startDate);
  const rows = tasks.map((task) => {
    const first = startDay.add(task.week - 1, 'week');
    const dates = task.everyDays
      ? listUserRecurringDates({
          startDate: first.toDate(),
          interval: task.everyDays,
          endType: 'until',
          untilDate: endDay.toDate(),
        })
      : [first];
    return { ...task, dates };
  });
  const occurrences = rows.flatMap((row) =>
    row.dates.map((day) => ({
      taskId: row.id,
      nameKey: row.nameKey,
      day,
      everyDays: row.everyDays || 0,
    }))
  );
  return {
    startDay,
    endDay,
    weeks: STARTER_PLAN_WEEKS,
    rows,
    occurrences,
  };
}

/**
 * Calendar events for the plan. Repeating tasks share one series config
 * so later edits and "delete all dates" stay in step with the saved days.
 */
export function buildStarterEvents(startDate, { plantId, nameFor }) {
  const plan = expandStarterPlan(startDate);
  const events = [];
  for (const row of plan.rows) {
    const name = nameFor(row);
    const todo = `TO DO: ${name}`;
    const recurring = row.everyDays
      ? {
          enabled: true,
          interval: row.everyDays,
          unit: 'days',
          endType: 'until',
          untilDate: plan.endDay.valueOf(),
        }
      : null;
    for (const day of row.dates) {
      events.push({
        title: todo,
        toDo: todo,
        actions: [],
        labels: plantId ? [plantId] : [],
        day: day.valueOf(),
        completed: false,
        isRecurringTodo: Boolean(recurring),
        createdFromAction: Boolean(recurring),
        ...(recurring
          ? {
              userRecurringConfig: recurring,
              recurringInterval: row.everyDays,
              recurringUnit: 'days',
            }
          : {}),
      });
    }
  }
  return events;
}

export function starterChoiceKey(uid) {
  return `${CHOICE_PREFIX}${uid}`;
}

export function readStarterChoice(uid) {
  if (!uid || typeof localStorage === 'undefined') return null;
  try {
    return localStorage.getItem(starterChoiceKey(uid));
  } catch {
    return null;
  }
}

export function writeStarterChoice(uid, choice) {
  if (!uid || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(starterChoiceKey(uid), choice);
  } catch {
    // Storage can be blocked; the calendar contents still decide the next visit.
  }
}

export function isRecentAccount(user, now = Date.now()) {
  const created = Date.parse(user?.metadata?.creationTime || '');
  if (!Number.isFinite(created)) return false;
  const age = now - created;
  return age >= -60 * 1000 && age <= STARTER_PLAN_WINDOW_MS;
}

export function shouldOfferStarterPlan({ user, eventsReady, eventCount, now = Date.now() }) {
  if (!user?.uid || !eventsReady || eventCount > 0) return false;
  if (readStarterChoice(user.uid)) return false;
  return isRecentAccount(user, now);
}
